import Box from "@mui/material/Box";
import { useTranslationApp } from "../../lib/i18next";
import {
  ALERT_ICON_TYPE_ERROR,
  AUDIO_FORMATS,
  FILE_FORMATS,
  IMAGE_FORMATS,
  MAX_SIZE_FILES,
  MAX_SIZE_FILES_VALUE_DISPLAY,
  MAX_SIZE_IMAGE,
  MAX_SIZE_IMAGE_VALUE_DISPLAY,
  VIDEO_FORMATS,
} from "../../utils/constants";
import { SimpleAlert } from "../Alerts/Alerts";
import {
  BUTTON_CHANGE_CONTENT,
  BUTTON_UPLOAD_CONTENT,
  ERROR,
  FIELD_FILE_MAX_SIZE,
  FIELD_FILE_VALID_REQUIRED,
  FIELD_MEDIA_FILE_MIN_DIMENSIONS,
} from "../../i18n/keysTranslations";
import { UploadFilesContext } from "../../contexts/UploadFilesContext";
import { useContext } from "react";
import { getDimensionsFromFileHtml } from "../../utils/string";
import Button from "./Button";

const UploadFileButton = ({
  id = "upload-media",
  value,
  name,
  disabled = false,
  icon,
  onChange,
  onFileSelect = () => {},
  textUpload = BUTTON_UPLOAD_CONTENT,
  colorUpload = "primary",
  textRemove = BUTTON_CHANGE_CONTENT,
  colorRemove = "secondary",
  isHidden = false,
  disableVerificationMedia = false,

  acceptedFormats = [],
  multiple,
  modeUpload = "sync",
  minDimensions = {},

  pathRTDB,
  pathStorage,
  loading = false,
  onCallbackLoading = () => {},
  onUploadSettled = () => {},
  sx = {},
  type,
}) => {
  const { t } = useTranslationApp();
  const { uploadFile } = useContext(UploadFilesContext) || {};

  const getPropsByValue = () => {
    if (value) {
      return {
        loading,
        color: colorUpload,
        variant: "contained",
      };
    }

    return {
      loading,
      color: colorRemove,
      variant: "contained",
    };
  };

  const props = getPropsByValue();

  const validateFiles = async (files) => {
    let hasFileNotAccepted = false;
    let hasFileToBig = false;
    let hasFileToSmall = false;
    let valueFileToBig = 0;

    for (const file of files) {
      const fileFormat = file.type.split("/")[1];
      const fileName = file.name.split(".")[1];

      if (
        !acceptedFormats.includes(fileName) &&
        !acceptedFormats.includes(fileFormat)
      ) {
        hasFileNotAccepted = true;
        break;
      }

      const isImage = IMAGE_FORMATS.includes(fileFormat);
      const MAX_SIZE = isImage ? MAX_SIZE_IMAGE : MAX_SIZE_FILES;
      const VALUE_DISPLAY = isImage
        ? MAX_SIZE_IMAGE_VALUE_DISPLAY
        : MAX_SIZE_FILES_VALUE_DISPLAY;

      if (file.size > MAX_SIZE) {
        hasFileToBig = true;
        valueFileToBig = VALUE_DISPLAY;
        break;
      }

      if (
        (IMAGE_FORMATS.includes(fileFormat) ||
          VIDEO_FORMATS.includes(fileFormat)) &&
        minDimensions?.width &&
        minDimensions?.height
      ) {
        const urlBlob = URL.createObjectURL(file);

        const { width, height } = await getDimensionsFromFileHtml({
          urlBlob,
          file,
        });

        if (width < minDimensions?.width || height < minDimensions?.height) {
          hasFileToSmall = true;
          break;
        }
      }
    }

    if (hasFileNotAccepted) {
      SimpleAlert({
        title: t(ERROR),
        text: t(FIELD_FILE_VALID_REQUIRED, {
          value: acceptedFormats.join(", "),
        }),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return false;
    }
    if (hasFileToBig) {
      SimpleAlert({
        title: t(ERROR),
        text: t(FIELD_FILE_MAX_SIZE, {
          value: valueFileToBig,
          type: "MB",
        }),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return false;
    }

    if (hasFileToSmall) {
      SimpleAlert({
        title: t(ERROR),
        text: t(FIELD_MEDIA_FILE_MIN_DIMENSIONS, {
          width: minDimensions.width,
          height: minDimensions.height,
        }),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return false;
    }

    return true;
  };

  const onChangeFile = async (e) => {
    const files = e.target.files;

    if (files.length === 0) return;

    if (onFileSelect) {
      onFileSelect(files);
    }

    try {
      const isValidFiles = await validateFiles(files);
      if (!isValidFiles) {
        return;
      }

      let arrayUrls = [];
      if (modeUpload === "async") {
        for (const file of files) {
          let preview = URL.createObjectURL(file);

          preview = `${preview}?ext=${file.type.split("/")[1]}`;

          arrayUrls.push(preview);
        }
        onChange(arrayUrls);
      }

      const uploadedUrls = [];
      let index = 0;
      for (const file of files) {
        const urlUploaded =
          modeUpload === "sync"
            ? await uploadFile({
                file,
                pathStorage,
                pathRTDB,
                onCallbackLoading,
                doCompression: true,
                modeUpload,
                urlPreview: arrayUrls[index],
                disableVerificationMedia,
              })
            : uploadFile({
                file,
                pathStorage,
                pathRTDB,
                onCallbackLoading,
                doCompression: true,
                modeUpload,
                urlPreview: arrayUrls[index],
                disableVerificationMedia,
              });

        index++;

        if (modeUpload === "sync" && urlUploaded) {
          uploadedUrls.push(urlUploaded);
        }
      }

      if (modeUpload === "sync" && uploadedUrls.length > 0) {
        await onChange(uploadedUrls);
      }
    } finally {
      const input = document.getElementById(id);
      if (input?.value) {
        input.value = "";
      }
      onUploadSettled();
    }
    return true;
  };

  if (modeUpload === "async" && !pathRTDB)
    throw new Error("When modeUpload is async, pathRTDB is required");

  if (type === "icon") {
    return (
      <Box
        sx={{
          display: isHidden ? "none" : "flex",
        }}
      >
        <Button
          {...props}
          type="button"
          component="label"
          disabled={disabled}
          sx={{
            ...sx,
          }}
        >
          {icon && !loading && icon}
          <input
            id={id}
            multiple={multiple}
            accept={acceptedFormats
              .map((item) => {
                if (IMAGE_FORMATS.includes(item)) return `image/${item},`;
                if (AUDIO_FORMATS.includes(item)) return `audio/${item},`;
                if (FILE_FORMATS.includes(item)) return `.${item},`;
                return `video/${item},`;
              })
              .join(" ")}
            name={name}
            type="file"
            hidden
            onChange={onChangeFile}
          />
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: isHidden ? "none" : "flex",
      }}
    >
      <Button
        {...props}
        type="button"
        component={"label"}
        disabled={disabled}
        endIcon={icon && !loading && icon}
        sx={sx}
      >
        {t(value ? textRemove : textUpload)}
        <input
          id={id}
          multiple={multiple}
          accept={acceptedFormats
            .map((item) => {
              if (IMAGE_FORMATS.includes(item)) return `image/${item},`;
              if (AUDIO_FORMATS.includes(item)) return `audio/${item},`;
              if (FILE_FORMATS.includes(item)) return `.${item},`;
              return `video/${item},`;
            })
            .join(" ")}
          name={name}
          type="file"
          hidden
          onChange={onChangeFile}
        />
      </Button>
    </Box>
  );
};

export default UploadFileButton;
